import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import Navbar from './Navbar';

const Organizations = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [createClientDialog, setCreateClientDialog] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'client',
    parent_org_id: null
  });
  
  // Client form state (includes user creation)
  const [clientFormData, setClientFormData] = useState({
    name: '',
    user_email: '',
    user_password: '',
    user_password_confirm: ''
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  useEffect(() => {
    fetchOrganizations();
    if (user && user.role === 'agency') {
      fetchClients();
    }
  }, [user]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations || []);
      } else {
        console.error('Failed to fetch organizations:', await response.text());
        showSnackbar('Failed to fetch organizations', 'error');
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      showSnackbar('Error loading organizations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use the agency/clients endpoint for fetching clients
      const response = await fetch(`${API_URL}/api/agency/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.clients)) {
          setClients(data.clients || []);
        } else {
          console.warn('Unexpected response format for clients:', data);
          setClients([]);
        }
      } else {
        console.error('Failed to fetch clients:', await response.text());
        showSnackbar('Failed to fetch clients', 'error');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      showSnackbar('Error loading clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations([...organizations, data.organization]);
        setCreateDialog(false);
        resetForm();
        showSnackbar('Organization created successfully', 'success');
        fetchOrganizations(); // Refresh the list
      } else {
        console.error('Failed to create organization:', await response.text());
        showSnackbar('Failed to create organization', 'error');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      showSnackbar('Error creating organization', 'error');
    }
  };

  const handleCreateClient = async () => {
    try {
      const token = localStorage.getItem('token');
      const requestData = {
        name: clientFormData.name,
        email: clientFormData.user_email
      };
      
      // Using the correct API endpoint for client creation
      const response = await fetch(`${API_URL}/api/organizations/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (user.role === 'agency') {
          fetchClients(); // Refresh the client list for agencies
        } else {
          fetchOrganizations(); // Refresh the main list for admins
        }
        setCreateClientDialog(false);
        resetClientForm();
        
        // Show success message with password info if provided
        if (data.client?.user?.password) {
          const successMessage = `Client created successfully. Password: ${data.client.user.password}`;
          showSnackbar(successMessage, 'success');
        } else {
          showSnackbar('Client created successfully', 'success');
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to create client:', errorData);
        showSnackbar(errorData.error || 'Failed to create client', 'error');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      showSnackbar('Error creating client', 'error');
    }
  };

  const handleUpdateOrg = async () => {
    if (!selectedOrg) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${selectedOrg.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        const updatedOrgs = organizations.map(org => 
          org.id === selectedOrg.id ? data.organization : org
        );
        setOrganizations(updatedOrgs);
        setEditDialog(false);
        resetForm();
        showSnackbar('Organization updated successfully', 'success');
      } else {
        console.error('Failed to update organization:', await response.text());
        showSnackbar('Failed to update organization', 'error');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      showSnackbar('Error updating organization', 'error');
    }
  };

  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${selectedOrg.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const updatedOrgs = organizations.filter(org => org.id !== selectedOrg.id);
        setOrganizations(updatedOrgs);
        setDeleteDialog(false);
        showSnackbar('Organization deleted successfully', 'success');
      } else {
        console.error('Failed to delete organization:', await response.text());
        showSnackbar('Failed to delete organization', 'error');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      showSnackbar('Error deleting organization', 'error');
    }
  };

  const handleEditClick = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      type: org.type,
      parent_org_id: org.parent_org_id
    });
    setEditDialog(true);
  };

  const handleDeleteClick = (org) => {
    setSelectedOrg(org);
    setDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'client',
      parent_org_id: null
    });
    setSelectedOrg(null);
  };

  const resetClientForm = () => {
    setClientFormData({
      name: '',
      user_email: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleClientInputChange = (e) => {
    const { name, value } = e.target;
    setClientFormData({
      ...clientFormData,
      [name]: value
    });
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Determine what to render based on user role
  const renderContent = () => {
    if (!user) return null;

    if (user.role === 'admin') {
      // Admin can see all organizations
      return (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">All Organizations</Typography>
            <Box>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setCreateDialog(true)}
                sx={{ mr: 1 }}
              >
                New Organization
              </Button>
              <Button 
                variant="contained" 
                color="secondary" 
                startIcon={<AddIcon />} 
                onClick={() => setCreateClientDialog(true)}
              >
                New Client
              </Button>
            </Box>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Parent</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>{org.id}</TableCell>
                    <TableCell>{org.name}</TableCell>
                    <TableCell>{org.type}</TableCell>
                    <TableCell>
                      {org.parent_org_id ? 
                        organizations.find(o => o.id === org.parent_org_id)?.name || org.parent_org_id 
                        : 'None'}
                    </TableCell>
                    <TableCell>{org.status}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditClick(org)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteClick(org)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
    } else if (user.role === 'agency') {
      // Agency can only see its clients
      return (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">My Clients</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setCreateClientDialog(true)}
            >
              New Client
            </Button>
          </Box>
          
          {clients.length === 0 ? (
            <Alert severity="info">
              You don't have any clients yet. Click the "New Client" button to create one.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>{client.id}</TableCell>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.status}</TableCell>
                      <TableCell>{new Date(client.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditClick(client)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      );
    } else {
      // Client can only see their own organization info
      return (
        <Alert severity="info">
          You are logged in as a client user. This area is for managing organizations and is only accessible to admins and agencies.
        </Alert>
      );
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 4, pb: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Organizations Management
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={user?.role === 'agency' ? fetchClients : fetchOrganizations}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderContent()
      )}

      {/* Create Organization Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)}>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="name"
            label="Organization Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              name="type"
              value={formData.type}
              label="Type"
              onChange={handleInputChange}
            >
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="agency">Agency</MenuItem>
              {user?.role === 'admin' && <MenuItem value="admin">Admin</MenuItem>}
            </Select>
          </FormControl>
          {formData.type === 'client' && user?.role === 'admin' && (
            <FormControl fullWidth>
              <InputLabel>Parent Agency</InputLabel>
              <Select
                name="parent_org_id"
                value={formData.parent_org_id || ''}
                label="Parent Agency"
                onChange={handleInputChange}
              >
                <MenuItem value="">None</MenuItem>
                {organizations
                  .filter(org => org.type === 'agency')
                  .map(agency => (
                    <MenuItem key={agency.id} value={agency.id}>{agency.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateOrg} 
            variant="contained" 
            color="primary"
            disabled={!formData.name}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={createClientDialog} onClose={() => setCreateClientDialog(false)}>
        <DialogTitle>Create New Client</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
            Enter the client organization name and email. A secure password will be auto-generated and sent to the provided email.
          </Typography>
          <TextField
            margin="dense"
            name="name"
            label="Client Name"
            fullWidth
            variant="outlined"
            value={clientFormData.name}
            onChange={handleClientInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="user_email"
            label="Client Email"
            type="email"
            fullWidth
            variant="outlined"
            value={clientFormData.user_email}
            onChange={handleClientInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateClientDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateClient}
            variant="contained"
            disabled={!clientFormData.name || !clientFormData.user_email}
          >
            Create Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="name"
            label="Organization Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            sx={{ mb: 2, mt: 1 }}
          />
          {user?.role === 'admin' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  label="Type"
                  onChange={handleInputChange}
                >
                  <MenuItem value="client">Client</MenuItem>
                  <MenuItem value="agency">Agency</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              {formData.type === 'client' && (
                <FormControl fullWidth>
                  <InputLabel>Parent Agency</InputLabel>
                  <Select
                    name="parent_org_id"
                    value={formData.parent_org_id || ''}
                    label="Parent Agency"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {organizations
                      .filter(org => org.type === 'agency')
                      .map(agency => (
                        <MenuItem key={agency.id} value={agency.id}>{agency.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateOrg} 
            variant="contained" 
            color="primary"
            disabled={!formData.name}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the organization: <strong>{selectedOrg?.name}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone and will delete all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteOrg} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Organizations; 